export type StorableValue = string | number | boolean | null | object;

const parseLocalStorageValue = <T = unknown>(value: unknown): T | null => {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') return value as T;
  if (value === '') return value as T;

  const trimmedValue = value.trim();
  const shouldParse =
    (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) ||
    (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) ||
    (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) ||
    ['true', 'false', 'null'].includes(trimmedValue) ||
    /^-?\d+(\.\d+)?$/.test(trimmedValue);

  if (!shouldParse) return value as T;

  try {
    return JSON.parse(trimmedValue) as T;
  } catch {
    return value as T;
  }
};

const normalizeChromeStorageValue = <T = unknown>(value: unknown): T | null => {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') return value as T;

  const trimmedValue = value.trim();
  if (!trimmedValue.startsWith('"') || !trimmedValue.endsWith('"')) {
    return value as T;
  }

  try {
    const parsedValue = JSON.parse(trimmedValue);
    return typeof parsedValue === 'string' ? parsedValue as T : value as T;
  } catch {
    return value as T;
  }
};

const serializeStoredValue = (value: StorableValue): string => {
  return typeof value === 'string' ? value : JSON.stringify(value);
};

// Helper to detect available storage
const getStorageAPI = () => {
  // Check for Chrome extension API
  if (typeof chrome !== 'undefined' && chrome?.storage?.local) {
    return { type: 'chrome' as const, api: chrome.storage.local };
  }

  // Fallback to localStorage
  if (typeof Storage !== 'undefined' && localStorage) {
    return { type: 'local' as const, api: localStorage };
  }

  return { type: 'none' as const, api: null };
};

const canUseLocalStorage = (): boolean => (
  typeof Storage !== 'undefined' && typeof localStorage !== 'undefined'
);

const setLocalStorageMirror = (key: string, value: StorableValue): void => {
  if (canUseLocalStorage()) {
    localStorage.setItem(key, serializeStoredValue(value));
  }
};

const removeLocalStorageMirror = (key: string): void => {
  if (canUseLocalStorage()) {
    localStorage.removeItem(key);
  }
};

export default {
  setStorageItem: async (key: string, value: StorableValue): Promise<void> => {
    const storage = getStorageAPI();

    try {
      if (storage.type === 'chrome') {
        await storage.api.set({ [key]: value });
        setLocalStorageMirror(key, value);
      } else if (storage.type === 'local') {
        storage.api.setItem(key, serializeStoredValue(value));
      } else {
        throw new Error('No storage API available');
      }
    } catch (error) {
      console.error('Error setting storage item:', error);
      // Fallback to localStorage if chrome storage fails
      if (storage.type === 'chrome' && canUseLocalStorage()) {
        setLocalStorageMirror(key, value);
      } else {
        throw error;
      }
    }
  },

  getStorageItem: async <T = unknown>(key: string): Promise<T | null> => {
    const storage = getStorageAPI();

    try {
      if (storage.type === 'chrome') {
        const result = await storage.api.get([key]);
        const value = result[key];
        const normalizedValue = normalizeChromeStorageValue<T>(value);

        if (normalizedValue === null && canUseLocalStorage()) {
          const localValue = localStorage.getItem(key);
          const parsedLocalValue = parseLocalStorageValue<T>(localValue);
          if (parsedLocalValue !== null) {
            await storage.api.set({ [key]: parsedLocalValue });
            setLocalStorageMirror(key, parsedLocalValue as StorableValue);
          }
          return parsedLocalValue;
        }

        if (typeof value === 'string' && typeof normalizedValue === 'string' && value !== normalizedValue) {
          await storage.api.set({ [key]: normalizedValue });
        }

        if (normalizedValue !== null) {
          setLocalStorageMirror(key, normalizedValue as StorableValue);
        }

        return normalizedValue;
      } else if (storage.type === 'local') {
        const value = storage.api.getItem(key);
        const parsedValue = parseLocalStorageValue<T>(value);
        if (typeof value === 'string' && typeof parsedValue === 'string' && value !== parsedValue) {
          storage.api.setItem(key, parsedValue);
        }
        return parsedValue;
      }
      return null;
    } catch (error) {
      console.error('Error getting storage item:', error);
      // Fallback to localStorage
      if (storage.type === 'chrome' && canUseLocalStorage()) {
        const value = localStorage.getItem(key);
        const parsedValue = parseLocalStorageValue<T>(value);
        if (typeof value === 'string' && typeof parsedValue === 'string' && value !== parsedValue) {
          localStorage.setItem(key, parsedValue);
        }
        return parsedValue;
      }
      return null;
    }
  },

  itemExistsInStorage: async (key: string): Promise<boolean> => {
    const storage = getStorageAPI();

    try {
      if (storage.type === 'chrome') {
        const result = await storage.api.get([key]);
        return result && key in result;
      } else if (storage.type === 'local') {
        return storage.api.getItem(key) !== null;
      }
      return false;
    } catch (error) {
      console.error('Error checking storage item:', error);
      // Fallback to localStorage
      if (storage.type === 'chrome' && canUseLocalStorage()) {
        return localStorage.getItem(key) !== null;
      }
      return false;
    }
  },

  removeStorageItem: async (key: string): Promise<boolean> => {
    const storage = getStorageAPI();

    try {
      if (storage.type === 'chrome') {
        await storage.api.remove([key]);
        removeLocalStorageMirror(key);
        return true;
      } else if (storage.type === 'local') {
        storage.api.removeItem(key);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing storage item:', error);
      // Fallback to localStorage
      if (storage.type === 'chrome' && canUseLocalStorage()) {
        removeLocalStorageMirror(key);
        return true;
      }
      return false;
    }
  },

  // Get info about which storage is being used
  getStorageInfo: () => {
    const storage = getStorageAPI();
    return {
      type: storage.type,
      available: storage.type !== 'none',
      isExtension: storage.type === 'chrome'
    };
  }
};
